import { useState, useEffect, useCallback, useMemo, useRef } from 'react'

interface UsePaginatedFeedOptions<T> {
  items: T[]
  initialPageSize?: number // Default: 30
  autoLoadThreshold?: number // Default: 100 (auto-load until this many items)
  pageSize?: number // Default: 30 items per load
}

interface UsePaginatedFeedResult<T> {
  visibleItems: T[]
  hasMore: boolean
  isAutoLoading: boolean // true when loaded < autoLoadThreshold
  loadMore: () => void
  loadMoreRef: React.RefObject<HTMLDivElement | null> // Attach to sentinel element
  totalCount: number
  displayedCount: number // Actual number of items being displayed
}

export function usePaginatedFeed<T>({
  items,
  initialPageSize = 30,
  autoLoadThreshold = 100,
  pageSize = 30,
}: UsePaginatedFeedOptions<T>): UsePaginatedFeedResult<T> {
  const [loadedCount, setLoadedCount] = useState(initialPageSize)
  const loadMoreRef = useRef<HTMLDivElement>(null)

  // Calculate visible items - only slice what we need
  const visibleItems = useMemo(
    () => items.slice(0, loadedCount),
    [items, loadedCount],
  )

  const hasMore = useMemo(
    () => loadedCount < items.length,
    [loadedCount, items.length],
  )
  const isAutoLoading = useMemo(
    () => loadedCount < autoLoadThreshold && loadedCount < items.length,
    [loadedCount, autoLoadThreshold, items.length],
  )

  // Load more callback - memoized to prevent unnecessary effect triggers
  const loadMore = useCallback(() => {
    setLoadedCount((prev) => Math.min(prev + pageSize, items.length))
  }, [pageSize, items.length])

  // Intersection Observer for auto-loading
  useEffect(() => {
    // Only observe when auto-loading is active
    if (!isAutoLoading || !loadMoreRef.current) return

    const observer = new IntersectionObserver(
      (entries) => {
        // When sentinel enters viewport, load more
        if (entries[0].isIntersecting) {
          loadMore()
        }
      },
      {
        // Trigger 200px before sentinel is visible for smoother UX
        rootMargin: '200px',
      },
    )

    observer.observe(loadMoreRef.current)

    // Cleanup on unmount or when auto-loading completes
    return () => observer.disconnect()
  }, [isAutoLoading, loadMore])

  // Reset pagination when items change significantly (new filter/search)
  // This is an intentional state reset when external data changes
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setLoadedCount(initialPageSize)
  }, [items.length, initialPageSize])

  return {
    visibleItems,
    hasMore,
    isAutoLoading,
    loadMore,
    loadMoreRef,
    totalCount: items.length,
    displayedCount: visibleItems.length,
  }
}
